// src/utils/promemoriaRipetuti.js
// ═══════════════════════════════════════════════════════════════════════════
// PROMEMORIA RIPETUTI DELLE NOTE IMPORTANTI (rosse) — ottobre 2026, W406
// ═══════════════════════════════════════════════════════════════════════════
// Una nota rossa («Importante») può ricordarsi da sola, a intervalli scelti dalla persona:
//   • ogni 30 minuti, 1, 2 o 4 ore;
//   • ogni giorno a un'ora scelta;
//   • ogni settimana, in un giorno e a un'ora scelti;
// per 1 giorno, 1 settimana, 1 mese oppure finché la nota resta rossa.
// Solo dalle 7:00 alle 19:00 (niente avvisi di notte). Arriva nella campanella e come
// notifica sul dispositivo, MAI per email. Si ferma da solo quando la nota è archiviata,
// eliminata o non è più rossa.
// Il server controlla ogni mezz'ora (functions: promemoriaOrari): gli orari sono a :00 o :30.
// Orari come testo "AAAA-MM-GGTHH:MM" in ora italiana: si confrontano come testo.
// STESSO CODICE in functions/promemoriaRipetuti.js (il server non può importare da qui):
// se si cambia una regola, va cambiata in tutti e due (prove: demo/prove/prova_promemoria.mjs).
// ═══════════════════════════════════════════════════════════════════════════

export const DALLE = "07:00";
export const ALLE = "19:00";
export const INTERVALLI = [30, 60, 120, 240];
export const DURATE = { giorno: 1, settimana: 7, mese: 30, sempre: null };
export const GIORNI_SETTIMANA = ["", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];

const p2 = (n) => String(n).padStart(2, "0");
// orari «da parete» (ora italiana) trattati come UTC: niente sorprese col cambio dell'ora
const daChiave = (k) => Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10), +k.slice(11, 13), +k.slice(14, 16));
const aChiave = (ms) => { const d = new Date(ms); return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}T${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`; };
const conOra = (k, hhmm) => `${k.slice(0, 10)}T${hhmm}`;
const giornoDopo = (k) => aChiave(daChiave(conOra(k, "00:00")) + 86400000).slice(0, 10);
/** 1 = lunedì … 7 = domenica */
const giornoSettimana = (k) => { const g = new Date(daChiave(conOra(k, "00:00"))).getUTCDay(); return g === 0 ? 7 : g; };

/** Mezz'ora successiva (o uguale) a un orario: 10:07 → 10:30, 10:30 → 10:30. */
export function aMezzora(k) {
  const ms = daChiave(k), passo = 30 * 60000;
  return aChiave(Math.ceil(ms / passo) * passo);
}

/** Riporta un orario dentro la fascia 7:00-19:00: prima delle 7 → alle 7; dopo le 19 → domani alle 7. */
export function inFascia(k) {
  const ora = k.slice(11, 16);
  if (ora < DALLE) return conOra(k, DALLE);
  if (ora > ALLE) return `${giornoDopo(k)}T${DALLE}`;
  return k;
}

/** Ultimo giorno dei promemoria per una durata, a partire dal giorno della chiave. */
export function fineDurata(durata, daK) {
  const giorni = DURATE[durata];
  if (giorni === null || giorni === undefined) return null;
  return aChiave(daChiave(conOra(daK, "00:00")) + (giorni - 1) * 86400000).slice(0, 10);
}

/**
 * Prossimo avviso DOPO l'orario «dopo» (escluso). ripeti:
 *   { tipo: "intervallo", minuti }  · { tipo: "giorno", ora: "HH:MM" } · { tipo: "settimana", giorno: 1-7, ora: "HH:MM" }
 * fino: "AAAA-MM-GG" (ultimo giorno compreso) o null. Restituisce la chiave o null se finito.
 */
export function prossimoAvviso(ripeti, dopo, fino = null) {
  if (!ripeti || !dopo) return null;
  let k;
  if (ripeti.tipo === "intervallo") {
    const minuti = INTERVALLI.includes(Number(ripeti.minuti)) ? Number(ripeti.minuti) : 60;
    k = inFascia(aMezzora(aChiave(daChiave(dopo) + minuti * 60000)));
  } else if (ripeti.tipo === "giorno" || ripeti.tipo === "settimana") {
    const ora = String(ripeti.ora || "09:00");
    k = conOra(dopo, ora);
    if (k <= dopo) k = `${giornoDopo(dopo)}T${ora}`;
    if (ripeti.tipo === "settimana") {
      const g = Math.min(7, Math.max(1, Number(ripeti.giorno) || 1));
      for (let i = 0; i < 7 && giornoSettimana(k) !== g; i++) k = `${giornoDopo(k)}T${ora}`;
    }
    k = inFascia(k);
  } else return null;
  if (fino && k.slice(0, 10) > fino) return null;
  return k;
}

/** «ogni 2 ore», «ogni giorno alle 9:00», «ogni lunedì alle 8:30» */
export function testoRipeti(ripeti) {
  if (!ripeti) return "";
  if (ripeti.tipo === "intervallo") return ripeti.minuti === 30 ? "ogni 30 minuti" : ripeti.minuti === 60 ? "ogni ora" : `ogni ${ripeti.minuti / 60} ore`;
  if (ripeti.tipo === "giorno") return `ogni giorno alle ${ripeti.ora}`;
  if (ripeti.tipo === "settimana") return `ogni ${GIORNI_SETTIMANA[ripeti.giorno] || "settimana"} alle ${ripeti.ora}`;
  return "";
}

/** Orari scelti per «ogni giorno» e «ogni settimana»: dalle 7:00 alle 19:00, ogni mezz'ora. */
export const ORARI_FASCIA = Array.from({ length: 25 }, (_, i) => `${p2(7 + Math.floor(i / 2))}:${i % 2 ? "30" : "00"}`);

/** «Adesso» come chiave in ora italiana. */
export function adessoChiave(d = new Date()) {
  const parti = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(d).map(p => [p.type, p.value]));
  return `${parti.year}-${parti.month}-${parti.day}T${parti.hour}:${parti.minute}`;
}
